import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Star, Trash2, ShoppingCart, Package, Euro, Building, RefreshCw, Pencil, AlertTriangle, X, Search, ArrowUpDown, Calculator, MapPin, Factory, ChevronDown, ChevronUp, Warehouse, Layers, Hash, User } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { formatEUR } from 'core/utils/format';

// Kontierungstyp → Badge-Farben (hell/dunkel), konsistent mit dem Warenkorb
// Labels werden zur Laufzeit über t() aufgelöst (siehe typeMeta-Funktion unten)
const TYPE_META_COLORS = {
  kostenstelle: { light: 'bg-emerald-100 text-emerald-700 border-emerald-200', dark: 'bg-emerald-600/20 text-emerald-300 border-emerald-400/30' },
  auftrag: { light: 'bg-violet-100 text-violet-700 border-violet-200', dark: 'bg-violet-600/20 text-violet-300 border-violet-400/30' },
  material: { light: 'bg-brand/10 text-brand border-brand/30', dark: 'bg-blue-600/20 text-blue-300 border-blue-400/30' },
  psp: { light: 'bg-amber-100 text-amber-700 border-amber-200', dark: 'bg-amber-600/20 text-amber-300 border-amber-400/30' },
  // dienstleistung = Altbestand (seit 29.07. kein Kontierungstyp mehr) — Anzeige bleibt lesbar
  dienstleistung: { light: 'bg-amber-100 text-amber-700 border-amber-200', dark: 'bg-amber-600/20 text-amber-300 border-amber-400/30' },
};

const SORT_OPTIONS_KEYS = [
  { value: 'recent', key: 'favorites.sortRecent' },
  { value: 'name', key: 'favorites.sortNameAZ' },
  { value: 'price_desc', key: 'favorites.sortPriceDesc' },
  { value: 'price_asc', key: 'favorites.sortPriceAsc' },
];

const typeMeta = (typ, t) => {
  const colors = TYPE_META_COLORS[(typ || '').toLowerCase()];
  const labelMap = {
    kostenstelle: 'favorites.typeKostenstelle',
    auftrag: 'favorites.typeAuftrag',
    material: 'favorites.typeMaterial',
    psp: 'banf.type.psp',
    dienstleistung: 'favorites.typeDienstleistung',
  };
  const key = labelMap[(typ || '').toLowerCase()];
  const label = key ? t(key) : (typ || t('favorites.typeDefault'));
  if (colors) return { label, ...colors };
  return { label, light: 'bg-slate-100 text-slate-600 border-slate-200', dark: 'bg-gray-600/20 text-gray-300 border-gray-400/30' };
};

// Einzelne Favoriten-Karte (eigener State fürs Bild-Fallback)
const FavoriteCard = ({ favorite, isDarkMode, t, formatPrice, onAddToCart, onEdit, onDelete }) => {
  const [imgError, setImgError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fmtDate = (d) => { if (!d) return null; try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; } };
  const detailRow = (label, value) => value ? (
    <div className="flex justify-between gap-3">
      <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}>{label}</span>
      <span className={`text-right ${isDarkMode ? 'text-white/80' : 'text-slate-600'}`}>{value}</span>
    </div>
  ) : null;
  const data = favorite.item_data || {};
  const imageUrl = data.image_url || favorite.image_url;
  const hasImage = imageUrl && !imgError;
  const meta = typeMeta(favorite.kontierungstyp, t);
  const supplier = favorite.supplier || favorite.preferred_vendor;
  const vendorNo = data.vendor_material_number || favorite.vendor_material_number;

  const chip = (Icon, label, value) => {
    if (!value) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${
        isDarkMode ? 'bg-white/5 border-white/10 text-white/70' : 'bg-slate-50 border-slate-200 text-slate-600'
      }`}>
        <Icon className={`w-3 h-3 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}>{label}</span>
        <span>{value}</span>
      </span>
    );
  };

  return (
    <div className="relative group/item">
      <div className={`absolute inset-0 ${
        isDarkMode
          ? 'bg-gray-900/40 border border-white/10 backdrop-blur-2xl'
          : 'glass-light-card border border-slate-200'
      } rounded-2xl shadow-sm group-hover/item:shadow-md transition-all duration-300`}></div>

      <div className="relative p-4">
        <div className="flex items-start gap-4">
          {/* Bild */}
          <div className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border flex items-center justify-center ${
            isDarkMode ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'
          }`}>
            {hasImage ? (
              <img src={imageUrl} alt={favorite.name || ''} onError={() => setImgError(true)} className="w-full h-full object-cover" />
            ) : (
              <Package className={`w-8 h-8 ${isDarkMode ? 'text-white/25' : 'text-slate-300'}`} />
            )}
          </div>

          {/* Inhalt */}
          <div className="flex-1 min-w-0">
            {/* Titelzeile + Preis */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <h3 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`} title={favorite.name}>
                  {favorite.name}
                </h3>
                {supplier && (
                  <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{supplier}</p>
                )}
                <span className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded text-xs font-medium border ${isDarkMode ? meta.dark : meta.light}`}>
                  {meta.label}
                </span>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {formatPrice(favorite.price, favorite.currency)}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>/ {favorite.unit || 'ST'}</div>
              </div>
            </div>

            {/* Info-Chips (mehr Infos inline, volle Zeilenbreite) */}
            <div className={`flex flex-wrap gap-1.5 mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              {favorite.material_number
                ? chip(Package, t('favorites.chipMat'), favorite.material_number)
                : (vendorNo && chip(Package, t('favorites.chipVendorNo'), vendorNo))}
              {favorite.manufacturer && chip(Factory, '', favorite.manufacturer)}
              {favorite.cost_center && chip(Building, t('favorites.chipKst'), favorite.cost_center)}
              {favorite.gl_account && chip(Calculator, t('favorites.chipGlAccount'), favorite.gl_account)}
              {favorite.plant && chip(MapPin, t('favorites.chipPlant'), favorite.plant)}
              {favorite.storage_location && chip(Warehouse, t('favorites.chipStorage'), favorite.storage_location)}
              {favorite.product_group && chip(Layers, t('favorites.chipProductGroup'), favorite.product_group)}
              {favorite.quantity && chip(Hash, t('favorites.chipQuantity'), `${favorite.quantity} ${favorite.unit || 'ST'}`)}
              {favorite.anforderer && chip(User, t('favorites.chipRequester'), favorite.anforderer)}
            </div>

            {/* Aktionen */}
            <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <button
                onClick={() => onAddToCart(favorite)}
                className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors"
              >
                <ShoppingCart className="w-4 h-4" />
                {t('favorites.addToCart')}
              </button>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className={`flex items-center gap-1 px-2.5 py-2 rounded-xl text-xs font-medium transition-colors ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white/60' : 'bg-slate-50 hover:bg-slate-100 text-slate-500'}`}
                >
                  {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {expanded ? t('favorites.detLess') : t('favorites.detMore')}
                </button>
                <button
                  onClick={() => onEdit(favorite)}
                  title={t('favorites.editFavorite')}
                  className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(favorite.id)}
                  title={t('favorites.delete')}
                  className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'bg-red-600/20 hover:bg-red-600/40 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-600'}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Ausklappbare Details */}
            {expanded && (
              <div className={`mt-3 pt-3 border-t text-xs space-y-1.5 max-w-xl ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                {detailRow(t('favorites.detDescription'), favorite.description || data.longtext)}
                {detailRow(t('favorites.detDeliveryDate'), fmtDate(data.delivery_date))}
                {detailRow(t('favorites.detManufacturerPartNo'), favorite.manufacturer_part_number || data.manufacturer_part_number)}
                {favorite.kontierungstyp === 'auftrag' && detailRow(t('favorites.detOrder'), favorite.order_number || data.auftrag)}
                {detailRow(t('favorites.detPurchasingOrg'), favorite.einkaueferorganisation)}
                {detailRow(t('favorites.detPurchasingGroup'), favorite.einkauefergruppe)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FavoritesView = ({ user, onBack, addToCart, showSuccess, showError, onEditFavorite }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFavorites = async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const response = await fetch('/api/favorites', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.ok) {
        setFavorites(await response.json());
      } else {
        showError?.(t('favorites.loadError'));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      showError?.(t('favorites.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (favoriteId) => setDeleteConfirmId(favoriteId);
  const handleDeleteCancel = () => setDeleteConfirmId(null);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      const response = await fetch(`/api/favorites/${deleteConfirmId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.ok) {
        setFavorites(prev => prev.filter(f => f.id !== deleteConfirmId));
        showSuccess?.(t('favorites.deleted'));
      } else {
        showError?.(t('favorites.deleteError'));
      }
    } catch (error) {
      console.error('Error deleting favorite:', error);
      showError?.(t('favorites.deleteError'));
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleAddToCart = (favorite) => {
    const data = favorite.item_data || {};
    const item = {
      id: `fav-${favorite.id}-${Date.now()}`,
      description: favorite.name,
      longDescription: favorite.description,
      quantity: favorite.quantity || 1,
      unit: favorite.unit || 'ST',
      price: favorite.price || 0,
      currency: favorite.currency || 'EUR',
      supplier: favorite.supplier,
      supplierId: favorite.supplier_id,
      materialNumber: favorite.material_number,
      productGroup: favorite.product_group,
      cost_center: favorite.cost_center,
      plant: favorite.plant,
      storage_location: favorite.storage_location,
      gl_account: favorite.gl_account,
      order_number: favorite.order_number,
      wbs_element: favorite.wbs_element,
      asset_number: favorite.asset_number,
      network_number: favorite.network_number,
      preferred_vendor: favorite.preferred_vendor,
      manufacturer: favorite.manufacturer,
      manufacturer_part_number: favorite.manufacturer_part_number,
      einkauefergruppe: favorite.einkauefergruppe,
      einkaueferorganisation: favorite.einkaueferorganisation,
      anforderer: favorite.anforderer,
      kontierungstyp: favorite.kontierungstyp || 'kostenstelle',
      // Produktbild aus dem gespeicherten item_data übernehmen
      image_url: data.image_url || favorite.image_url || '',
      // Merge with stored item_data if available
      ...data
    };
    if (addToCart) {
      addToCart(item);
      showSuccess?.(t('favorites.addToCart'));
    }
  };

  const handleEditFavorite = (favorite) => {
    if (onEditFavorite) onEditFavorite(favorite);
  };

  const formatPrice = (price, currency) => formatEUR(price, currency);

  // Verfügbare Kontierungstypen für die Filter-Chips
  const availableTypes = useMemo(() => {
    const set = new Set(favorites.map(f => (f.kontierungstyp || '').toLowerCase()).filter(Boolean));
    return Array.from(set);
  }, [favorites]);

  // Filtern (Suche + Typ) und sortieren
  const visibleFavorites = useMemo(() => {
    const terms = query.toLowerCase().split(/[\s,;]+/).filter(Boolean);
    let list = favorites.filter(f => {
      if (typeFilter !== 'all' && (f.kontierungstyp || '').toLowerCase() !== typeFilter) return false;
      if (terms.length === 0) return true;
      const hay = [f.name, f.supplier, f.preferred_vendor, f.material_number, f.manufacturer, f.cost_center, f.gl_account, f.plant]
        .filter(Boolean).join(' ').toLowerCase();
      return terms.every(term => hay.includes(term));
    });
    const sorted = [...list];
    if (sortBy === 'name') sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
    else if (sortBy === 'price_asc') sorted.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    else if (sortBy === 'price_desc') sorted.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    // 'recent' = Reihenfolge vom Server (created_at DESC)
    return sorted;
  }, [favorites, query, typeFilter, sortBy]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-white'}`}>
      {/* Header */}
      <div className="relative">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl'
          : 'bg-white border-b border-slate-200 shadow-sm'
        }`}></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`${isDarkMode ? 'bg-amber-600/30 border border-white/20' : 'bg-brand/10'} w-10 h-10 rounded-xl flex items-center justify-center`}>
                <Star className={`w-5 h-5 ${isDarkMode ? 'text-amber-300' : 'text-brand'}`} />
              </div>
              <div>
                <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('favorites.title')}</h1>
                <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('favorites.count', { count: favorites.length })}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={loadFavorites}
                disabled={loading}
                className={`px-4 py-2 rounded-xl flex items-center font-medium transition-colors ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {t('favorites.refresh')}
              </button>
              <button
                onClick={onBack}
                className={`px-4 py-2 rounded-xl flex items-center font-medium transition-colors ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('common.back')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent"></div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900/30 border border-white/20 backdrop-blur-2xl' : 'glass-light-card border border-slate-200'} rounded-3xl`}></div>
            <div className="relative p-12 text-center">
              <div className={`relative mx-auto mb-6 ${isDarkMode ? 'bg-amber-600/30 border border-white/20' : 'bg-brand/10'} w-20 h-20 rounded-3xl flex items-center justify-center`}>
                <Star className={`w-10 h-10 ${isDarkMode ? 'text-amber-300' : 'text-brand'}`} />
              </div>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-3`}>{t('favorites.empty')}</h3>
              <p className={`${isDarkMode ? 'text-white/60' : 'text-slate-500'} max-w-md mx-auto`}>{t('favorites.emptyDesc')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar: Suche + Sortierung + Typ-Filter */}
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('favorites.search')}
                    className={`w-full pl-9 pr-3 py-2 rounded-xl text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors ${isDarkMode ? 'bg-gray-800/40 text-white border-white/10 placeholder-white/30' : 'bg-white text-slate-800 border-slate-200 placeholder-slate-300'}`}
                  />
                </div>
                <div className="relative">
                  <ArrowUpDown className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`pl-9 pr-8 py-2 rounded-xl text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors appearance-none ${isDarkMode ? 'bg-gray-800/40 text-white border-white/10' : 'bg-white text-slate-800 border-slate-200'}`}
                  >
                    {SORT_OPTIONS_KEYS.map(o => <option key={o.value} value={o.value}>{t(o.key)}</option>)}
                  </select>
                </div>
              </div>

              {availableTypes.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {['all', ...availableTypes].map(typ => {
                    const active = typeFilter === typ;
                    const label = typ === 'all' ? t('favorites.filterAll') : typeMeta(typ, t).label;
                    return (
                      <button
                        key={typ}
                        onClick={() => setTypeFilter(typ)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? 'bg-brand text-white border-brand'
                            : isDarkMode ? 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Ergebnis-Grid */}
            {visibleFavorites.length === 0 ? (
              <p className={`text-center py-12 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('favorites.noMatch')}</p>
            ) : (
              <div className="space-y-3">
                {visibleFavorites.map(favorite => (
                  <FavoriteCard
                    key={favorite.id}
                    favorite={favorite}
                    isDarkMode={isDarkMode}
                    t={t}
                    formatPrice={formatPrice}
                    onAddToCart={handleAddToCart}
                    onEdit={handleEditFavorite}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-slate-900/40'} backdrop-blur-sm flex items-center justify-center p-4 z-50`}>
          <div className={`relative w-full max-w-md rounded-2xl border ${isDarkMode ? 'bg-gray-900 border-white/10' : 'bg-white border-slate-200'} shadow-xl`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center space-x-3">
                  <div className={`${isDarkMode ? 'bg-amber-600/30' : 'bg-amber-100'} w-11 h-11 rounded-xl flex items-center justify-center`}>
                    <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`} />
                  </div>
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('favorites.delete')}</h2>
                </div>
                <button onClick={handleDeleteCancel} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-slate-100 text-slate-400'}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className={`${isDarkMode ? 'text-white/80' : 'text-slate-600'} mb-6`}>{t('favorites.deleteConfirm')}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCancel}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-colors ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2.5 px-4 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesView;
